import { BarChart, LineChart } from 'echarts/charts';
import { GridComponent, LegendComponent, TooltipComponent } from 'echarts/components';
import * as echarts from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { useEffect, useRef, useState, type ReactNode } from 'react';

// Only what the app draws. The full `echarts` bundle is several times larger.
echarts.use([BarChart, LineChart, GridComponent, TooltipComponent, LegendComponent, CanvasRenderer]);

/** An ECharts option, kept loose here: each chart builds its own. */
export type EChartOption = Record<string, unknown>;

export interface EChartClick {
  seriesId?: string;
  dataIndex: number;
}

export interface EChartProps {
  option: EChartOption;
  height: number;
  /** A click on a mark: which series, and which point along the axis. */
  onClick?: (params: EChartClick) => void;
}

/** Reads `data-theme` off <html>, which theme-mode.ts sets for both themes. */
function currentTheme(): string {
  return document.documentElement.getAttribute('data-theme') ?? 'light';
}

/**
 * Swaps every `var(--token)` string in an option for its value now.
 *
 * A canvas cannot read CSS custom properties, and tokens.css is the only
 * place a colour may be written down. So a chart names its colours as tokens
 * and they are resolved here, again whenever the theme changes.
 */
function resolveTokens(value: unknown, styles: CSSStyleDeclaration): unknown {
  if (typeof value === 'string') {
    const match = /^var\((--[a-z0-9-]+)\)$/.exec(value);
    if (match?.[1] === undefined) return value;
    const resolved = styles.getPropertyValue(match[1]).trim();
    return resolved.length > 0 ? resolved : value;
  }
  if (Array.isArray(value)) return value.map((item) => resolveTokens(item, styles));
  if (value !== null && typeof value === 'object') {
    return Object.fromEntries(Object.entries(value).map(([k, v]) => [k, resolveTokens(v, styles)]));
  }
  return value;
}

/**
 * The one place the app touches ECharts (CLAUDE.md §3: dashboards in AntD +
 * ECharts). Owns the instance: created on mount, disposed on unmount, resized
 * with its container, redrawn when the theme flips.
 */
export function EChart({ option, height, onClick }: EChartProps): ReactNode {
  const host = useRef<HTMLDivElement>(null);
  const chart = useRef<echarts.ECharts | null>(null);
  const clickRef = useRef(onClick);
  const [theme, setTheme] = useState(currentTheme);

  useEffect(() => {
    clickRef.current = onClick;
  }, [onClick]);

  useEffect(() => {
    if (host.current === null) return undefined;
    const instance = echarts.init(host.current);
    chart.current = instance;
    instance.on('click', (params: unknown) => {
      const p = params as { seriesId?: string; dataIndex: number };
      clickRef.current?.({ ...(p.seriesId === undefined ? {} : { seriesId: p.seriesId }), dataIndex: p.dataIndex });
    });

    const resize = (): void => {
      instance.resize();
    };
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(resize);
    observer?.observe(host.current);

    const themeWatch =
      typeof MutationObserver === 'undefined'
        ? null
        : new MutationObserver(() => {
            setTheme(currentTheme());
          });
    themeWatch?.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });

    return () => {
      observer?.disconnect();
      themeWatch?.disconnect();
      instance.dispose();
      chart.current = null;
    };
  }, []);

  useEffect(() => {
    const styles = getComputedStyle(document.documentElement);
    // notMerge: a series that went away (a filter, a year with no data) must
    // not linger from the previous option.
    chart.current?.setOption(resolveTokens(option, styles) as EChartOption, true);
  }, [option, theme]);

  return <div ref={host} style={{ width: '100%', height }} />;
}
